/**
 * Marketplace verbs in the command palette (marketplace.md section 2):
 * "Try AI Scribe", "Turn on Reviews", "Manage Warp 9 Texting", and
 * "Request ..." for staff without `marketplace.purchase`. Each opens the
 * module page with the right sheet ready (`?trial=1` or the tab).
 */
import type { CatalogModule, StoreResponse } from '@velora/contracts/marketplace';
import { MARKETPLACE_CATALOG } from '@velora/contracts/marketplace';
import type { SearchProvider, SearchResult } from '../../app/commands/registry.js';
import { marketplaceApi } from './api.js';
import { scopeSummary } from './model.js';

/**
 * Client-side navigation from outside React (palette results). Pushes the
 * URL through the History API and lets the router pick it up from the
 * `popstate` it listens to, keeping the office selector.
 */
export function navigateTo(path: string, search: Record<string, string> = {}): void {
  const current = new URL(window.location.href);
  const url = new URL(path, window.location.origin);
  const office = current.searchParams.get('office');
  if (office) url.searchParams.set('office', office);
  for (const [k, v] of Object.entries(search)) url.searchParams.set(k, v);
  const prev = (window.history.state ?? {}) as Record<string, unknown>;
  const index = typeof prev['__TSR_index'] === 'number' ? (prev['__TSR_index'] as number) + 1 : 0;
  const state = { ...prev, __TSR_index: index, key: Math.random().toString(36).slice(2, 10) };
  window.history.pushState(state, '', url);
  window.dispatchEvent(new PopStateEvent('popstate', { state }));
}

let cache: { at: number; store: StoreResponse } | null = null;

async function loadStore(signal: AbortSignal): Promise<StoreResponse | null> {
  if (cache && Date.now() - cache.at < 30_000) return cache.store;
  try {
    const store = await marketplaceApi.store(signal);
    cache = { at: Date.now(), store };
    return store;
  } catch {
    return null;
  }
}

/** Ranked palette entries for a query; exported for tests. */
export function paletteResults(
  query: string,
  store: StoreResponse | null,
  scopeIds: readonly string[] | null,
): {
  module: CatalogModule;
  verb: 'try' | 'turn-on' | 'manage' | 'request' | 'open';
  label: string;
}[] {
  const q = query.trim().toLowerCase();
  const wantsVerb = /^(turn on|try|start|enable|cancel|turn off|manage|request)\b/.test(q);
  const term = q.replace(/^(turn on|turn off|try|start|enable|cancel|manage|request)\s*/, '');
  const out: {
    module: CatalogModule;
    verb: 'try' | 'turn-on' | 'manage' | 'request' | 'open';
    label: string;
    score: number;
  }[] = [];
  for (const m of MARKETPLACE_CATALOG) {
    const name = m.name.toLowerCase();
    const short = m.shortName.toLowerCase();
    const hit =
      term.length === 0
        ? wantsVerb
        : name.startsWith(term) || short.startsWith(term)
          ? 3
          : name.includes(term) || m.valueLine.toLowerCase().includes(term)
            ? 1
            : 0;
    if (!hit) continue;
    const sm = store?.modules.find((x) => x.key === m.key);
    const ids = scopeIds ?? store?.offices.map((o) => o.id) ?? [];
    const s = sm ? scopeSummary(sm, ids, store?.timeZone ?? 'UTC', Date.now()) : null;
    const canBuy = store?.canPurchase ?? false;
    let verb: 'try' | 'turn-on' | 'manage' | 'request' | 'open';
    if (!canBuy) verb = s && s.on > 0 ? 'open' : 'request';
    else if (/^(cancel|turn off|manage)/.test(q) || (s && s.on > 0 && !s.trialAvailable))
      verb = s?.anyManageable ? 'manage' : 'open';
    else if (/^(try|start)/.test(q) || (s?.trialAvailable && !/^(turn on|enable)/.test(q)))
      verb = s?.trialAvailable ? 'try' : 'turn-on';
    else verb = 'turn-on';
    const label =
      verb === 'try'
        ? `Try ${m.name} free`
        : verb === 'turn-on'
          ? `Turn on ${m.name}`
          : verb === 'manage'
            ? `Manage ${m.name}`
            : verb === 'request'
              ? `Request ${m.name}`
              : `Open ${m.name}`;
    out.push({
      module: m,
      verb,
      label,
      score: (typeof hit === 'number' ? hit : 1) * 10 + (wantsVerb ? 5 : 0),
    });
  }
  return out.sort((a, b) => b.score - a.score).map(({ score: _s, ...r }) => r);
}

export const marketplaceSearchProvider: SearchProvider = {
  id: 'marketplace',
  heading: 'Marketplace',
  minLength: 2,
  order: 60,
  patients: false,
  async search(query, { signal, office }): Promise<readonly SearchResult[]> {
    const store = await loadStore(signal);
    const scope = office === 'all' || !store ? null : [office];
    return paletteResults(query, store, scope)
      .slice(0, 5)
      .map((r) => ({
        id: `marketplace.${r.verb}.${r.module.key}`,
        label: r.label,
        hint: `${r.module.valueLine.slice(0, 64)}${r.module.valueLine.length > 64 ? '...' : ''}`,
        icon: 'store',
        keywords: ['marketplace', 'module', 'add-on'],
        run: () => {
          cache = null;
          if (r.verb === 'try' || r.verb === 'request')
            navigateTo(`/marketplace/${r.module.key}`, { trial: '1' });
          else if (r.verb === 'turn-on')
            navigateTo(`/marketplace/${r.module.key}`, { turnon: '1' });
          else if (r.verb === 'manage') navigateTo(`/marketplace/${r.module.key}`, { manage: '1' });
          else navigateTo(`/marketplace/${r.module.key}`);
        },
      }));
  },
};
